import type { GlobalRoute } from "../types/routerTypes";
import { routes } from "./routes";

/**
 * A menu category label (e.g. "Profiles", "Advanced Configs") that has no page of its own,
 * so it renders as plain text in the breadcrumb trail.
 */
export type BreadcrumbGroup = Readonly<{
    kind: "group";
    labelKey: string;
}>;

/**
 * A specific parent record (e.g. the Application Profile a Card Profile belongs to).
 * `paramName` is the route param on the CURRENTLY matched route that holds that parent's id;
 * `route` is the parent's own edit route, used to build the link by substituting `:id` with that value.
 */
export type BreadcrumbParentResource = Readonly<{
    kind: "parentResource";
    labelKey: string;
    paramName: string;
    route: GlobalRoute;
}>;

export type BreadcrumbAncestor = BreadcrumbGroup | BreadcrumbParentResource;

export type BreadcrumbConfigEntry = Readonly<{
    /** Label for this resource's own crumb. */
    labelKey: string;
    /** Link for this resource's own crumb (e.g. back to its list page). Omit if it has no standalone page. */
    to?: string;
    /**
     * For nested resources with no standalone list page, where the "list" is actually a tab within the
     * immediate parent's edit page (mirrors `setTabsDefaultBeforeBack` in that resource's own
     * Create/Edit page component). The self crumb links to the last `parentResource` ancestor's
     * record and activates this Redux tab key before navigating there.
     */
    selfTabKey?: string;
    /** Crumbs rendered before this resource's own crumb, outermost first. */
    ancestors?: ReadonlyArray<BreadcrumbAncestor>;
}>;

const PROFILES_GROUP: BreadcrumbGroup = { kind: "group", labelKey: "menu.profiles" };
const ADVANCED_CONFIGS_GROUP: BreadcrumbGroup = { kind: "group", labelKey: "menu.advancedConfigs" };
const CAMPAIGN_MANAGEMENT_GROUP: BreadcrumbGroup = { kind: "group", labelKey: "menu.campaignManagement" };
const SYSTEM_CONFIGURATION_GROUP: BreadcrumbGroup = { kind: "group", labelKey: "menu.systemConfiguration" };

const applicationProfileEft4fAncestor: BreadcrumbParentResource = {
    kind: "parentResource",
    labelKey: "menu.applicationsProfiles",
    paramName: "appProfileId",
    route: routes.applicationProfilesEdit,
};

const applicationProfileApacAncestor: BreadcrumbParentResource = {
    kind: "parentResource",
    labelKey: "menu.applicationsProfiles",
    paramName: "appProfileId",
    route: routes.applicationProfilesApacEdit,
};

const cardProfileEft4fAncestor: BreadcrumbParentResource = {
    kind: "parentResource",
    labelKey: "menu.cardProfiles",
    paramName: "cardProfId",
    route: routes.cardProfilesEditEft4F,
};

const emvKernelProfileApacAncestor: BreadcrumbParentResource = {
    kind: "parentResource",
    labelKey: "menu.emvKernelProfiles",
    paramName: "emvKernelProfileId",
    route: routes.emvKernelProfilesApacEdit,
};

const cardImplementationAncestor: BreadcrumbParentResource = {
    kind: "parentResource",
    labelKey: "menu.cardImplementations",
    paramName: "cardImplementationId",
    route: routes.cardImplementationsEdit,
};

const cardDefinitionAncestor: BreadcrumbParentResource = {
    kind: "parentResource",
    labelKey: "menu.cardDefinitions",
    paramName: "cardDefinitionId",
    route: routes.cardDefinitionsEdit,
};

const acquirerVirtualAncestor: BreadcrumbParentResource = {
    kind: "parentResource",
    labelKey: "menu.acquirer",
    paramName: "id",
    route: routes.acquirersEditVirtual,
};

/**
 * Static breadcrumb ancestry for every create/edit route, keyed by that route's path pattern
 * (matched against the current location via `matchPath` in {@link useBreadcrumbs}).
 */
export const breadcrumbConfig: Readonly<Record<string, BreadcrumbConfigEntry>> = {
    // Top-level resources (no parent menu group)
    [routes.siteGroupsCreate.path]: { labelKey: "menu.siteGroups", to: routes.siteGroups.fillPathParams() },
    [routes.siteGroupsEdit.path]: { labelKey: "menu.siteGroups", to: routes.siteGroups.fillPathParams() },
    [routes.sitesCreate.path]: { labelKey: "menu.sites", to: routes.sites.fillPathParams() },
    [routes.sitesEdit.path]: { labelKey: "menu.sites", to: routes.sites.fillPathParams() },
    [routes.virtualSiteGroupsCreate.path]: { labelKey: "menu.virtualSiteGroups", to: routes.virtualSiteGroups.fillPathParams() },
    [routes.virtualSiteGroupsEdit.path]: { labelKey: "menu.virtualSiteGroups", to: routes.virtualSiteGroups.fillPathParams() },
    [routes.contractsPhysicalCreate.path]: { labelKey: "menu.contracts", to: routes.contracts.fillPathParams() },
    [routes.contractsPhysicalEdit.path]: { labelKey: "menu.contracts", to: routes.contracts.fillPathParams() },
    [routes.contractsVirtualCreate.path]: { labelKey: "menu.contracts", to: routes.contracts.fillPathParams() },
    [routes.contractsVirtualEdit.path]: { labelKey: "menu.contracts", to: routes.contracts.fillPathParams() },
    [routes.terminalsCreate.path]: { labelKey: "menu.terminals", to: routes.terminals.fillPathParams() },
    [routes.terminalsEdit.path]: { labelKey: "menu.terminals", to: routes.terminals.fillPathParams() },

    // Profiles
    [routes.productProfilesCreate.path]: {
        labelKey: "menu.productProfiles",
        to: routes.productProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.productProfilesEdit.path]: {
        labelKey: "menu.productProfiles",
        to: routes.productProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.productsCreate.path]: { labelKey: "menu.products", to: routes.products.fillPathParams(), ancestors: [PROFILES_GROUP] },
    [routes.productsEdit.path]: { labelKey: "menu.products", to: routes.products.fillPathParams(), ancestors: [PROFILES_GROUP] },
    [routes.dialogProfilesCreate.path]: {
        labelKey: "menu.dialogProfiles",
        to: routes.dialogProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.dialogProfilesEdit.path]: {
        labelKey: "menu.dialogProfiles",
        to: routes.dialogProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.applicationProfilesCreate.path]: {
        labelKey: "menu.applicationsProfiles",
        to: routes.applicationProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.applicationProfilesEdit.path]: {
        labelKey: "menu.applicationsProfiles",
        to: routes.applicationProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.applicationProfilesApacCreate.path]: {
        labelKey: "menu.applicationsProfiles",
        to: routes.applicationProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.applicationProfilesApacEdit.path]: {
        labelKey: "menu.applicationsProfiles",
        to: routes.applicationProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.cardTypesCreate.path]: { labelKey: "menu.cardTypes", to: routes.cardTypes.fillPathParams(), ancestors: [PROFILES_GROUP] },
    [routes.cardTypesEdit.path]: { labelKey: "menu.cardTypes", to: routes.cardTypes.fillPathParams(), ancestors: [PROFILES_GROUP] },
    [routes.contractProfilesCreate.path]: {
        labelKey: "menu.contractProfiles",
        to: routes.contractProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.contractProfilesEdit.path]: {
        labelKey: "menu.contractProfiles",
        to: routes.contractProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.vatCodesCreate.path]: { labelKey: "menu.vatCodes", to: routes.vatCodes.fillPathParams(), ancestors: [PROFILES_GROUP] },
    [routes.vatCodesEdit.path]: { labelKey: "menu.vatCodes", to: routes.vatCodes.fillPathParams(), ancestors: [PROFILES_GROUP] },
    [routes.promptDataProfilesCreate.path]: {
        labelKey: "menu.promptDataProfiles",
        to: routes.promptDataProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },
    [routes.promptDataProfilesEdit.path]: {
        labelKey: "menu.promptDataProfiles",
        to: routes.promptDataProfiles.fillPathParams(),
        ancestors: [PROFILES_GROUP],
    },

    // Card Profiles, nested under a specific Application Profile
    [routes.cardProfilesCreateEft4F.path]: {
        labelKey: "menu.cardProfiles",
        selfTabKey: "applications-profiles_cardProfiles",
        ancestors: [PROFILES_GROUP, applicationProfileEft4fAncestor],
    },
    [routes.cardProfilesEditEft4F.path]: {
        labelKey: "menu.cardProfiles",
        selfTabKey: "applications-profiles_cardProfiles",
        ancestors: [PROFILES_GROUP, applicationProfileEft4fAncestor],
    },
    [routes.cardProfilesCreateApac.path]: {
        labelKey: "menu.cardProfiles",
        selfTabKey: "applications-profiles_cardProfiles",
        ancestors: [PROFILES_GROUP, applicationProfileApacAncestor],
    },
    [routes.cardProfilesEditApac.path]: {
        labelKey: "menu.cardProfiles",
        selfTabKey: "applications-profiles_cardProfiles",
        ancestors: [PROFILES_GROUP, applicationProfileApacAncestor],
    },

    // Prompt Data, nested under a specific (EFT4F) Card Profile
    [routes.promptDataCreate.path]: {
        labelKey: "menu.promptData",
        ancestors: [PROFILES_GROUP, applicationProfileEft4fAncestor, cardProfileEft4fAncestor],
    },
    [routes.promptDataEdit.path]: {
        labelKey: "menu.promptData",
        ancestors: [PROFILES_GROUP, applicationProfileEft4fAncestor, cardProfileEft4fAncestor],
    },

    // Advanced Configs
    [routes.acquirersCreatePhysical.path]: {
        labelKey: "menu.acquirer",
        to: routes.acquirers.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.acquirersEditPhysical.path]: {
        labelKey: "menu.acquirer",
        to: routes.acquirers.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.acquirersCreateVirtual.path]: {
        labelKey: "menu.acquirer",
        to: routes.acquirers.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.acquirersEditVirtual.path]: {
        labelKey: "menu.acquirer",
        to: routes.acquirers.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.hostProductCodesCreate.path]: {
        labelKey: "menu.hostProductCodes",
        to: routes.hostProductCodes.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.hostProductCodesEdit.path]: {
        labelKey: "menu.hostProductCodes",
        to: routes.hostProductCodes.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.hostConfigurationsCreate.path]: {
        labelKey: "menu.hostConfigurations",
        to: routes.hostConfigurations.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.hostConfigurationsEdit.path]: {
        labelKey: "menu.hostConfigurations",
        to: routes.hostConfigurations.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.terminalProfilesCreate.path]: {
        labelKey: "menu.terminalProfiles",
        to: routes.terminalProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.terminalProfilesEdit.path]: {
        labelKey: "menu.terminalProfiles",
        to: routes.terminalProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.cardImplementationsCreate.path]: {
        labelKey: "menu.cardImplementations",
        to: routes.cardImplementations.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.cardImplementationsEdit.path]: {
        labelKey: "menu.cardImplementations",
        to: routes.cardImplementations.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.posMappingsCreate.path]: {
        labelKey: "menu.posMappings",
        to: routes.posMappings.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.posMappingsEdit.path]: {
        labelKey: "menu.posMappings",
        to: routes.posMappings.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.posTypesCreate.path]: { labelKey: "menu.posTypes", to: routes.posTypes.fillPathParams(), ancestors: [ADVANCED_CONFIGS_GROUP] },
    [routes.posTypesEdit.path]: { labelKey: "menu.posTypes", to: routes.posTypes.fillPathParams(), ancestors: [ADVANCED_CONFIGS_GROUP] },
    // [routes.terminalTypesCreate.path]: {
    //     labelKey: "menu.terminalTypes",
    //     to: routes.terminalTypes.fillPathParams(),
    //     ancestors: [ADVANCED_CONFIGS_GROUP],
    // },
    // [routes.terminalTypesEdit.path]: {
    //     labelKey: "menu.terminalTypes",
    //     to: routes.terminalTypes.fillPathParams(),
    //     ancestors: [ADVANCED_CONFIGS_GROUP],
    // },
    [routes.emvKernelProfilesCreate.path]: {
        labelKey: "menu.emvKernelProfiles",
        to: routes.emvKernelProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.emvKernelProfilesEdit.path]: {
        labelKey: "menu.emvKernelProfiles",
        to: routes.emvKernelProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.emvKernelProfilesApacCreate.path]: {
        labelKey: "menu.emvKernelProfiles",
        to: routes.emvKernelProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.emvKernelProfilesApacEdit.path]: {
        labelKey: "menu.emvKernelProfiles",
        to: routes.emvKernelProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.allowedProductsProfilesCreate.path]: {
        labelKey: "menu.allowedProductsProfiles",
        to: routes.allowedProductsProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.allowedProductsProfilesEdit.path]: {
        labelKey: "menu.allowedProductsProfiles",
        to: routes.allowedProductsProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.hostResponseCodesCreate.path]: {
        labelKey: "menu.hostResponseCodes",
        to: routes.hostResponseCodes.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.hostResponseCodesEdit.path]: {
        labelKey: "menu.hostResponseCodes",
        to: routes.hostResponseCodes.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.printingProfilesCreate.path]: {
        labelKey: "menu.printingProfiles",
        to: routes.printingProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.printingProfilesEdit.path]: {
        labelKey: "menu.printingProfiles",
        to: routes.printingProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.dccProfilesCreate.path]: {
        labelKey: "menu.dccProfiles",
        to: routes.dccProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },
    [routes.dccProfilesEdit.path]: {
        labelKey: "menu.dccProfiles",
        to: routes.dccProfiles.fillPathParams(),
        ancestors: [ADVANCED_CONFIGS_GROUP],
    },

    // Card Implementation -> Card Definitions -> Card Attributes
    [routes.cardDefinitionsCreate.path]: {
        labelKey: "menu.cardDefinitions",
        selfTabKey: "card-implementations_cardDefinitions",
        ancestors: [ADVANCED_CONFIGS_GROUP, cardImplementationAncestor],
    },
    [routes.cardDefinitionsEdit.path]: {
        labelKey: "menu.cardDefinitions",
        selfTabKey: "card-implementations_cardDefinitions",
        ancestors: [ADVANCED_CONFIGS_GROUP, cardImplementationAncestor],
    },
    [routes.cardAttributesCreate.path]: {
        labelKey: "menu.cardAttributes",
        selfTabKey: "card-implementations_cardAttributes",
        ancestors: [ADVANCED_CONFIGS_GROUP, cardImplementationAncestor, cardDefinitionAncestor],
    },
    [routes.cardAttributesEdit.path]: {
        labelKey: "menu.cardAttributes",
        selfTabKey: "card-implementations_cardAttributes",
        ancestors: [ADVANCED_CONFIGS_GROUP, cardImplementationAncestor, cardDefinitionAncestor],
    },

    // EMV Kernel Profile (APAC) -> AID Preferences / AID Preferences Cless / EMV Configurations / CAP keys
    [routes.aidPreferencesCreate.path]: {
        labelKey: "menu.aidPreferences",
        selfTabKey: "emv-kernel-profiles_aidPreferences",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },
    [routes.aidPreferencesEdit.path]: {
        labelKey: "menu.aidPreferences",
        selfTabKey: "emv-kernel-profiles_aidPreferences",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },
    [routes.aidPreferencesClessCreate.path]: {
        labelKey: "menu.aidPreferencesCless",
        selfTabKey: "emv-kernel-profiles_aidPreferencesCless",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },
    [routes.aidPreferencesClessEdit.path]: {
        labelKey: "menu.aidPreferencesCless",
        selfTabKey: "emv-kernel-profiles_aidPreferencesCless",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },
    [routes.emvConfigurationsCreate.path]: {
        labelKey: "menu.emvConfigurations",
        selfTabKey: "emv-kernel-profiles_emvConfigurations",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },
    [routes.emvConfigurationsEdit.path]: {
        labelKey: "menu.emvConfigurations",
        selfTabKey: "emv-kernel-profiles_emvConfigurations",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },
    [routes.capKeysCreate.path]: {
        labelKey: "menu.capKeys",
        selfTabKey: "emv-kernel-profiles_caPublicKeys",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },
    [routes.capKeysEdit.path]: {
        labelKey: "menu.capKeys",
        selfTabKey: "emv-kernel-profiles_caPublicKeys",
        ancestors: [ADVANCED_CONFIGS_GROUP, emvKernelProfileApacAncestor],
    },

    // Acquirer (Virtual) -> nested Physical Acquirer
    [routes.acquirersToVirtualCreate.path]: {
        labelKey: "menu.acquirer",
        selfTabKey: "acquirers_physicalAcquirers",
        ancestors: [ADVANCED_CONFIGS_GROUP, acquirerVirtualAncestor],
    },
    [routes.acquirersToVirtualEdit.path]: {
        labelKey: "menu.acquirer",
        selfTabKey: "acquirers_physicalAcquirers",
        ancestors: [ADVANCED_CONFIGS_GROUP, acquirerVirtualAncestor],
    },

    // Campaign Management
    [routes.campaignsOrgUnitsCreate.path]: {
        labelKey: "menu.campaigns",
        to: routes.campaigns.fillPathParams(),
        ancestors: [CAMPAIGN_MANAGEMENT_GROUP],
    },
    [routes.campaignsOrgUnitsEdit.path]: {
        labelKey: "menu.campaigns",
        to: routes.campaigns.fillPathParams(),
        ancestors: [CAMPAIGN_MANAGEMENT_GROUP],
    },
    [routes.campaignsTerminalsCreate.path]: {
        labelKey: "menu.campaigns",
        to: routes.campaigns.fillPathParams(),
        ancestors: [CAMPAIGN_MANAGEMENT_GROUP],
    },
    [routes.campaignsTerminalsEdit.path]: {
        labelKey: "menu.campaigns",
        to: routes.campaigns.fillPathParams(),
        ancestors: [CAMPAIGN_MANAGEMENT_GROUP],
    },
    [routes.alertsCreate.path]: { labelKey: "menu.alerts", to: routes.alerts.fillPathParams(), ancestors: [CAMPAIGN_MANAGEMENT_GROUP] },
    [routes.alertsEdit.path]: { labelKey: "menu.alerts", to: routes.alerts.fillPathParams(), ancestors: [CAMPAIGN_MANAGEMENT_GROUP] },

    // System Configuration
    [routes.dataGroupsCreate.path]: {
        labelKey: "menu.dataGroups",
        to: routes.dataGroups.fillPathParams(),
        ancestors: [SYSTEM_CONFIGURATION_GROUP],
    },
    [routes.dataGroupsEdit.path]: {
        labelKey: "menu.dataGroups",
        to: routes.dataGroups.fillPathParams(),
        ancestors: [SYSTEM_CONFIGURATION_GROUP],
    },
    [routes.dynamicPropertiesCreate.path]: {
        labelKey: "menu.dynamicProperties",
        to: routes.dynamicProperties.fillPathParams(),
        ancestors: [SYSTEM_CONFIGURATION_GROUP],
    },
    [routes.dynamicPropertiesEdit.path]: {
        labelKey: "menu.dynamicProperties",
        to: routes.dynamicProperties.fillPathParams(),
        ancestors: [SYSTEM_CONFIGURATION_GROUP],
    },
    [routes.dynamicPropertyGroupsCreate.path]: {
        labelKey: "menu.dynamicPropertyGroups",
        to: routes.dynamicPropertyGroups.fillPathParams(),
        ancestors: [SYSTEM_CONFIGURATION_GROUP],
    },
    [routes.dynamicPropertyGroupsEdit.path]: {
        labelKey: "menu.dynamicPropertyGroups",
        to: routes.dynamicPropertyGroups.fillPathParams(),
        ancestors: [SYSTEM_CONFIGURATION_GROUP],
    },
};
